import type { ReactNode } from 'react';
import { Printer } from 'lucide-react';
import type { ScanAgentCapabilities, ScanAgentDevice } from './scan-agent';
import type { ScanOptions } from './useScanAgent';

export interface ScanControlsProps {
  devices: ScanAgentDevice[];
  device: string;
  capabilities: ScanAgentCapabilities | null;
  options: ScanOptions;
  disabled: boolean;
  onDevice: (id: string) => void;
  onOptions: (change: Partial<ScanOptions>) => void;
  /** The scanner's own window chooses resolution, colour, paper and sides: Paige's are set aside. */
  ownWindow?: boolean;
  /** The last pill: More (the scanner's own window), when the scanner has one. */
  more?: ReactNode;
}

const fallbackResolutions = [150, 200, 300, 400, 600];
const colours: [ScanOptions['color'], string][] = [
  ['color', 'Colour'],
  ['grayscale', 'Greyscale'],
  ['blackAndWhite', 'Black and white'],
];
const sources: [ScanOptions['source'], string][] = [
  ['feeder', 'Feeder'],
  ['flatbed', 'Flatbed'],
];

/**
 * The scanner and how to scan, as one row of small choices under the scanner's name: resolution,
 * colour, where the paper comes from, both sides, blank pages left out. The labels are for screen
 * readers; the choices say what they are.
 */
export function ScanControls(props: ScanControlsProps): ReactNode {
  const { capabilities, options, disabled, ownWindow, onOptions } = props;
  const [scanner, ...settings] = fieldsFor(props);
  return (
    <div className="paige-scan-controls">
      {scanner && <Field {...scanner} />}
      <div
        className={`paige-scan-options${ownWindow ? ' is-theirs' : ''}`}
        role="group"
        aria-label="Scan settings"
      >
        {settings.map((field) => (
          <Field key={field.label} {...field} />
        ))}
        <Toggle
          label="Both sides"
          checked={options.duplex}
          disabled={disabled || ownWindow === true || capabilities?.duplex === false}
          onChange={(duplex) => onOptions({ duplex })}
        />
        <Toggle
          label="Skip blank pages"
          checked={options.skipBlank}
          disabled={disabled}
          onChange={(skipBlank) => onOptions({ skipBlank })}
        />
        {props.more}
      </div>
    </div>
  );
}

function Toggle(t: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}): ReactNode {
  return (
    <label className="paige-scan-toggle">
      <input
        type="checkbox"
        checked={t.checked}
        disabled={t.disabled}
        onChange={(e) => t.onChange(e.target.checked)}
      />
      {t.label}
    </label>
  );
}

/** The four selects: which scanner, and the three settings whose choices depend on it. */
function fieldsFor(props: ScanControlsProps): FieldProps[] {
  const { devices, device, capabilities, options, disabled, onDevice, onOptions } = props;
  const oneSource = capabilities ? capabilities.feeder !== capabilities.flatbed : false;
  const theirs = disabled || props.ownWindow === true;
  return [
    {
      label: 'Scanner',
      wide: true,
      value: device,
      options: devices.map((d) => [d.id, d.name]),
      disabled,
      onChange: onDevice,
    },
    {
      label: 'Resolution',
      value: options.dpi,
      options: (capabilities?.resolutions ?? fallbackResolutions).map((dpi) => [dpi, `${dpi} dpi`]),
      disabled: theirs,
      onChange: (dpi) => onOptions({ dpi: Number(dpi) }),
    },
    {
      label: 'Colour',
      value: options.color,
      options: colours,
      disabled: theirs,
      onChange: (color) => onOptions({ color: color as ScanOptions['color'] }),
    },
    {
      label: 'Paper from',
      value: options.source,
      options: sources,
      disabled: theirs || oneSource,
      onChange: (source) => onOptions({ source: source as ScanOptions['source'] }),
    },
  ];
}

interface FieldProps {
  label: string;
  value: string | number;
  options: [string | number, string][];
  disabled: boolean;
  wide?: boolean;
  onChange: (value: string) => void;
}

function Field(field: FieldProps): ReactNode {
  const { label, value, options, disabled, wide, onChange } = field;
  return (
    <label className={`paige-scan-field${wide ? ' is-wide' : ''}`}>
      {wide && <Printer size={18} aria-hidden="true" />}
      <span className="paige-scan-field-label">{label}</span>
      <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {options.map(([option, text]) => (
          <option key={option} value={option}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}
